/**
 * Reducer finite-state machine for the Adversarial Robustness Red-Team game.
 *
 * `reducer(state, intent) → state` is pure, deterministic, and React-free
 * (skill-74). The Play panel drives it with `useReducer`. There is no
 * render loop and no unseeded randomness: a given scenario + seed + intent
 * sequence always produces the same state.
 *
 * The reducer NEVER realizes a forbidden square the rules generator did not
 * offer. A `realize` intent for an honest-to-honest pair is a structural
 * no-op: the move was not in the legal set, so there is nothing to construct.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { GameIntent, GameState, Scenario } from "./types.js";
import {
  factorsThroughAdversary,
  isHonestHonest,
  isLegalSquare,
  pairKey,
} from "./rules.js";

/** Build the initial game state for a scenario (deterministic in `seed`). */
export function initialState(scenario: Scenario, seed = 1): GameState {
  return {
    scenarioId: scenario.id,
    seed,
    budget: defaultBudget(scenario),
    compromised: [],
    realized: [],
    status: `${scenario.agents.length} agents online. Every honest pair shares a governed interface. Compromise up to k agents and attempt a forbidden cross-agent square.`,
  };
}

/** A small default budget, well under n. */
export function defaultBudget(scenario: Scenario): number {
  return Math.min(2, maxBudget(scenario));
}

/** Maximum admissible budget: strictly less than n. */
export function maxBudget(scenario: Scenario): number {
  return Math.max(0, scenario.agents.length - 1);
}

function agentName(scenario: Scenario, id: string): string {
  return scenario.agents.find((agent) => agent.id === id)?.name ?? id;
}

/**
 * The pure transition function. Every branch returns a fresh state; the
 * input is never mutated.
 */
export function reducer(
  scenario: Scenario,
  state: GameState,
  intent: GameIntent,
): GameState {
  switch (intent.kind) {
    case "reset":
      return initialState(scenario, state.seed);

    case "setBudget": {
      const budget = clamp(intent.budget, 0, maxBudget(scenario));
      // Dropping the budget restores agents beyond the new ceiling, oldest
      // compromises kept (deterministic prefix).
      const compromised = state.compromised.slice(0, budget);
      const realized = pruneRealized(state, compromised);
      return {
        ...state,
        budget,
        compromised,
        realized,
        status: `Compromise budget set to k = ${String(budget)} of ${String(
          scenario.agents.length,
        )}.`,
      };
    }

    case "compromise": {
      if (state.compromised.includes(intent.agent)) return state;
      if (state.compromised.length >= state.budget) {
        return {
          ...state,
          status: `Budget exhausted: k = ${String(
            state.budget,
          )} agents already compromised. Restore one or raise k.`,
        };
      }
      const compromised = [...state.compromised, intent.agent];
      return {
        ...state,
        compromised,
        status: `${agentName(
          scenario,
          intent.agent,
        )} compromised. Its interfaces are now unbounded ALLOW; squares factoring through it become reachable.`,
      };
    }

    case "restore": {
      if (!state.compromised.includes(intent.agent)) return state;
      const compromised = state.compromised.filter((id) => id !== intent.agent);
      const realized = pruneRealized(state, compromised);
      return {
        ...state,
        compromised,
        realized,
        status: `${agentName(
          scenario,
          intent.agent,
        )} restored to honest. Squares that factored only through it are no longer reachable.`,
      };
    }

    case "realize": {
      const set = new Set(state.compromised);
      const key = pairKey(intent.a, intent.b);

      if (isHonestHonest(set, intent.a, intent.b)) {
        // The honest-to-honest composite is not in the legal move set. It is
        // structurally inexpressible (honest-agent isolation, RD-31 §4.2 /
        // RD-05 compositional-safety) — there is no move to realize.
        return {
          ...state,
          status: `This ${agentName(scenario, intent.a)} ↔ ${agentName(
            scenario,
            intent.b,
          )} composite is structurally inexpressible — not in the move set. Both agents validate at their governed interface, so no forbidden square exists to construct.`,
        };
      }

      if (!isLegalSquare(scenario, set, intent.a, intent.b)) {
        // No governed interface, or otherwise outside the reachable surface.
        return {
          ...state,
          status: `No reachable square between ${agentName(
            scenario,
            intent.a,
          )} and ${agentName(scenario, intent.b)} under the current compromise set.`,
        };
      }

      if (state.realized.some((v) => pairKey(v.a, v.b) === key)) {
        return {
          ...state,
          status: `Already realized: ${agentName(scenario, intent.a)} → ${agentName(
            scenario,
            intent.b,
          )} is on your confined surface.`,
        };
      }

      const factors = factorsThroughAdversary(set, intent.a, intent.b);
      const realized = [
        ...state.realized,
        { a: intent.a, b: intent.b, factorsThrough: factors },
      ];
      return {
        ...state,
        realized,
        status: `Forbidden square realized: ${agentName(scenario, intent.a)} → ${agentName(
          scenario,
          intent.b,
        )}, factoring through ${factors
          .map((id) => agentName(scenario, id))
          .join(
            ", ",
          )}. Every violation you can reach passes through a compromised agent.`,
      };
    }

    default:
      return state;
  }
}

/**
 * Recompute the realized surface against a new compromise set: each retained
 * violation's factor list is re-derived from the current set, and a violation
 * is dropped entirely once it no longer factors through any compromised
 * agent. This keeps the violation-confinement witness honest — every listed
 * factor is genuinely compromised in the resulting state.
 */
function pruneRealized(
  state: GameState,
  compromised: readonly string[],
): readonly GameState["realized"][number][] {
  const set = new Set(compromised);
  const kept: GameState["realized"][number][] = [];
  for (const v of state.realized) {
    const factors = v.factorsThrough.filter((id) => set.has(id));
    if (factors.length === 0) continue;
    kept.push({ a: v.a, b: v.b, factorsThrough: factors });
  }
  return kept;
}

function clamp(value: number, lo: number, hi: number): number {
  if (Number.isNaN(value)) return lo;
  return Math.max(lo, Math.min(hi, Math.trunc(value)));
}
